<!--
  Pineapple AdminSidebar
  - DesignSystem/admin-shared.jsx 의 AdminSidebar 를 Vue 로 이식.
  - NAV 는 현재 라우터에 실제 등록된 페이지만 노출
    (대시보드 / 학사 / 헬프데스크).
-->
<template>
  <aside class="p-sidebar" :class="{ 'is-collapsed': collapsed }">
    <!-- Brand -->
    <div class="p-sidebar__brand">
      <div class="p-sidebar__logo">
        <img src="/favicon.png" alt="Pineapple" />
      </div>
      <div class="p-sidebar__brand-text">
        <div class="p-sidebar__brand-name">pineapple</div>
        <div class="p-sidebar__brand-sub">INSTRUCTOR</div>
      </div>
    </div>

    <!-- Nav -->
    <nav class="p-sidebar__nav">
      <div
        v-for="cat in nav"
        :key="cat.cat"
        class="p-sidebar__cat"
      >
        <!-- 자식이 1개이고 이름이 카테고리와 동일 → 헤더가 곧 링크 (서브메뉴 숨김) -->
        <router-link
          v-if="catDirectTo(cat)"
          :to="catDirectTo(cat)"
          class="p-sidebar__cat-head"
          :class="{ 'is-active': isItemMatch(cat.items[0]) }"
        >
          <component :is="iconFor(cat.icon)" class="p-sidebar__cat-icon" />
          <span class="p-sidebar__cat-label">{{ cat.label }}</span>
        </router-link>

        <template v-else>
          <div
            class="p-sidebar__cat-head"
            :class="{ 'is-active': isCatActive(cat) }"
            @click="onCatClick(cat)"
          >
            <component :is="iconFor(cat.icon)" class="p-sidebar__cat-icon" />
            <span class="p-sidebar__cat-label">{{ cat.label }}</span>
            <span v-if="cat.items.length" class="p-sidebar__cat-count">{{ cat.items.length }}</span>
          </div>

          <div
            v-if="cat.items.length && (isCatActive(cat) || collapsed)"
            class="p-sidebar__items"
          >
            <div v-if="collapsed" class="p-sidebar__items-title">{{ cat.label }}</div>
            <router-link
              v-for="it in cat.items"
              :key="it.id"
              :to="it.to"
              class="p-sidebar__item"
              :class="{ 'is-active': isItemMatch(it) }"
            >
              <span v-if="isItemMatch(it)" class="p-sidebar__item-dot" />
              {{ it.label }}
            </router-link>
          </div>
        </template>
      </div>
    </nav>

    <!-- Footer (user) -->
    <div class="p-sidebar__footer">
      <div class="p-sidebar__avatar">{{ avatarInitials }}</div>
      <div class="p-sidebar__user">
        <div class="p-sidebar__user-name">{{ userName }}</div>
        <div class="p-sidebar__user-role">{{ roleLabel }} · ONLINE</div>
      </div>
      <button class="p-sidebar__logout" :title="$t('navi.logout')" @click="onLogout">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
      </button>
    </div>
  </aside>
</template>

<script>
import { h } from 'vue';

const svgProps = {
  viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor',
  'stroke-width': 1.6, 'stroke-linecap': 'round', 'stroke-linejoin': 'round',
};
const Icons = {
  home:  { render: () => h('svg', svgProps, [h('path', { d: 'M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z' })]) },
  cap:   { render: () => h('svg', svgProps, [
    h('path', { d: 'M22 10v6M2 10l10-5 10 5-10 5z' }),
    h('path', { d: 'M6 12v5c3 3 9 3 12 0v-5' }),
  ])},
  help:  { render: () => h('svg', svgProps, [
    h('circle', { cx: 12, cy: 12, r: 10 }),
    h('path', { d: 'M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3M12 17h.01' }),
  ])},
  book:  { render: () => h('svg', svgProps, [
    h('path', { d: 'M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z' }),
    h('path', { d: 'M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z' }),
  ])},
  building: { render: () => h('svg', svgProps, [
    h('rect', { x: 3, y: 3, width: 18, height: 18, rx: 2 }),
    h('path', { d: 'M9 21v-8h6v8M7 7h.01M12 7h.01M17 7h.01M7 11h.01M12 11h.01M17 11h.01' }),
  ])},
};

export default {
  name: 'PAdminSidebar',
  computed: {
    /** 현재 라우터에 등록된 페이지만 노출하는 사이드바 메뉴
     *  aliases: 해당 메뉴의 "자식/상세" 라우트 이름들 — 이 라우트에 있어도 부모 메뉴가 활성화됨 */
    nav() {
      return [
        {
          cat: 'dashboard', label: this.$t('navi.dash'), icon: 'home',
          items: [
            { id: 'sales', label: this.$t('navi.dash'), to: { name: 'Sales' } },
          ],
        },
        {
          cat: 'academic', label: this.$t('navi.05'), icon: 'cap',
          items: [
            { id: 'classes',      label: this.$t('navi.05_01'), to: { name: 'AcademicClasses' },     aliases: ['AcademicClassesDetail'] },
            { id: 'evaluations',  label: this.$t('navi.05_02'), to: { name: 'AcademicEvaluations' } },
            { id: 'questionBank', label: this.$t('navi.05_03'), to: { name: 'AcademicQuestionBank' } },
          ],
        },
        // 자료실 — 승인된 강사(userType === 'instructor')에게만 노출
        ...(this.isInstructor ? [{
          cat: 'materials', label: this.$t('navi.materials'), icon: 'book',
          items: [
            { id: 'materialsBooks', label: this.$t('navi.materials'), to: { name: 'MaterialsBooks' }, aliases: ['MaterialsBooksDetail'] },
          ],
        }] : []),
        // 기관 관리 — 기관 Master(GET /voucher/org/me 1건 이상)에게만 노출. 정본 docs/바우처_기관발행_강사화면.md §2
        ...(this.isOrgMaster ? [{
          cat: 'organization', label: this.$t('navi.org'), icon: 'building',
          items: [
            { id: 'orgInstructors', label: this.$t('navi.org_01'), to: { name: 'OrgInstructors' }, aliases: ['OrgInstructorClassrooms'] },
            { id: 'orgVouchers',    label: this.$t('navi.org_02'), to: { name: 'OrgVouchers' } },
          ],
        }] : []),
        {
          cat: 'helpDesk', label: this.$t('navi.07'), icon: 'help',
          items: [
            { id: 'faqs',    label: this.$t('navi.07_02'), to: { name: 'HelpDeskFAQs' } },
            { id: 'inquiry', label: this.$t('navi.07_03'), to: { name: 'HelpDeskInquiry' } },
          ],
        },
      ];
    },
    currentRouteName() {
      return this.$route.name;
    },
    userInfo() {
      return this.$store.state.auth?.user_info || {};
    },
    isInstructor() {
      return this.userInfo.userType === 'instructor';
    },
    isOrgMaster() {
      return this.$store.getters['organization/isMaster'];
    },
    userName() {
      return this.userInfo.name || this.userInfo.user_name || 'Admin';
    },
    avatarInitials() {
      const n = String(this.userName).trim();
      if (!n) return 'A';
      const ch = n.charAt(0).toUpperCase();
      return ch;
    },
    roleLabel() {
      return this.$store.getters['auth/getRoleName'] || 'Admin';
    },
    collapsed() {
      return this.$store.getters['layout/isSidebarCollapsed'];
    },
  },
  data() {
    return {
      // 동시에 단 하나의 카테고리만 열리도록 단일 키 보관 (null = 사용자 토글 없음)
      openCat: null,
    };
  },
  watch: {
    /** 라우트 이동 시 — 현재 라우트가 속한 카테고리만 열린 상태 유지 */
    currentRouteName: {
      immediate: true,
      handler() {
        const matched = this.nav.find(c => c.items.some(it => this.isItemMatch(it)));
        this.openCat = matched ? matched.cat : null;
      },
    },
  },
  methods: {
    iconFor(name) {
      return Icons[name] || Icons.home;
    },
    /** 현재 라우트가 해당 nav 아이템의 메인 라우트 또는 aliases 중 하나와 일치하는지 */
    isItemMatch(item) {
      const name = this.currentRouteName;
      if (!name) return false;
      if (item.to?.name === name) return true;
      if (Array.isArray(item.aliases) && item.aliases.includes(name)) return true;
      return false;
    },
    isCatActive(cat) {
      return this.openCat === cat.cat;
    },
    onCatClick(cat) {
      if (!cat.items.length) return;
      this.openCat = this.openCat === cat.cat ? null : cat.cat;
    },
    /** 자식이 1개이고 라벨이 카테고리와 동일하면 헤더가 바로 그 라우트로 이동 */
    catDirectTo(cat) {
      if (cat.items.length === 1 && cat.items[0].label === cat.label) {
        return cat.items[0].to;
      }
      return null;
    },
    onLogout() {
      this.$router.push({ name: 'Logout' });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/scss/_pineapple.scss';

.p-sidebar {
  width: $p-sidebar-w;
  flex-shrink: 0;
  background: $p-sidebar;
  color: $p-sidebar-tx;
  display: flex;
  flex-direction: column;
  border-right: 1px solid $p-sidebar-line;
  background-image: radial-gradient(circle at 20% 10%, rgba(255, 201, 60, 0.04) 0%, transparent 40%);
  font-family: $p-font-body;
  height: 100vh;
  position: sticky;
  top: 0;
  transition: width 0.2s ease;

  &__brand {
    padding: 16px 12px 14px;
    border-bottom: 1px solid $p-sidebar-line;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 16px;
    text-align: center;
    flex-shrink: 0;
  }
  &__logo {
    width: 52px;
    height: 52px;
    display: grid;
    place-items: center;
    flex-shrink: 0;

    > img {
      width: 100%;
      height: 100%;
      object-fit: contain;
      filter: drop-shadow(0 4px 12px rgba(255, 201, 60, 0.4));
    }
  }
  &__brand-text {
    min-width: 0;
    text-align: center;
    line-height: 1.1;
  }
  &__brand-name {
    font-size: 20px;
    font-weight: 900;
    letter-spacing: -0.02em;
    line-height: 1.1;
    color: $p-sidebar-tx;
  }
  &__brand-sub {
    font-size: 10px;
    color: $p-sidebar-mid;
    font-weight: 700;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    margin-top: 3px;
  }

  &__nav {
    flex: 1;
    overflow: auto;
    padding: 12px 10px;
    /* 스크롤바 슬림 */
    &::-webkit-scrollbar { width: 6px; }
    &::-webkit-scrollbar-thumb { background: $p-sidebar-alt; border-radius: 3px; }
  }

  &__cat { margin-bottom: 4px; }

  &__cat-head {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 14px;
    border-radius: 10px;
    color: $p-sidebar-tx;
    background: transparent;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    letter-spacing: -0.01em;
    white-space: nowrap;
    transition: background 0.15s ease, color 0.15s ease;

    &:hover { background: $p-sidebar-alt; }

    &.is-active {
      color: $p-point;
      background: $p-sidebar-alt;
      font-weight: 800;
      box-shadow: inset 3px 0 0 $p-point;
    }
  }
  &__cat-icon {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
  }
  &__cat-label { flex: 1; }
  &__cat-count {
    margin-left: auto;
    font-size: 10px;
    color: $p-sidebar-mid;
    font-family: $p-font-mono;
    font-weight: 600;

    .p-sidebar__cat-head.is-active & { color: $p-point-deep; }
  }

  &__items {
    padding: 2px 0 6px 30px;
  }
  &__item {
    display: block;
    padding: 6px 12px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 500;
    color: $p-sidebar-mid;
    background: transparent;
    cursor: pointer;
    position: relative;
    white-space: nowrap;
    text-decoration: none;
    transition: color 0.15s ease, background 0.15s ease;

    &:hover { color: $p-sidebar-tx; }

    &.is-active {
      color: $p-point;
      font-weight: 700;
      background: #2A2620;
    }
  }
  &__item-dot {
    position: absolute;
    left: -14px;
    top: 50%;
    transform: translateY(-50%);
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: $p-point;
  }

  &__footer {
    padding: 14px 20px;
    border-top: 1px solid $p-sidebar-line;
    display: flex;
    align-items: center;
    gap: 10px;
  }
  &__avatar {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    background: linear-gradient(135deg, $p-point 0%, $p-point-deep 100%);
    color: $p-sidebar;
    display: grid;
    place-items: center;
    font-size: 13px;
    font-weight: 900;
    flex-shrink: 0;
  }
  &__user {
    flex: 1;
    min-width: 0;
    line-height: 1.2;
  }
  &__user-name {
    font-size: 13px;
    font-weight: 800;
    color: $p-sidebar-tx;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  &__user-role {
    font-size: 10px;
    color: $p-sidebar-mid;
    margin-top: 1px;
  }
  &__logout {
    width: 24px;
    height: 24px;
    border-radius: 6px;
    border: 0;
    cursor: pointer;
    background: transparent;
    color: $p-sidebar-mid;
    display: grid;
    place-items: center;
    transition: color 0.15s ease;

    &:hover { color: $p-point; }
  }

  /* ─── 접힘(아이콘 전용) 모드 ─── */
  &.is-collapsed {
    width: 64px;

    .p-sidebar__brand {
      padding: 4px 6px;
      gap: 0;
    }
    .p-sidebar__logo {
      width: 48px;
      height: 48px;
    }
    .p-sidebar__brand-text { display: none; }

    .p-sidebar__nav { padding: 12px 8px; overflow: visible; }

    .p-sidebar__cat {
      position: relative;
      margin-bottom: 6px;
    }
    .p-sidebar__cat-head {
      padding: 12px;
      justify-content: center;
      gap: 0;

      &.is-active { box-shadow: inset 3px 0 0 $p-point; }
    }
    .p-sidebar__cat-label,
    .p-sidebar__cat-count { display: none; }

    /* 기본은 숨기고, hover 시 우측 플라이아웃으로 노출 */
    .p-sidebar__items {
      display: none;
      position: absolute;
      left: calc(100% + 4px);
      top: 0;
      min-width: 200px;
      padding: 8px;
      background: $p-sidebar-alt;
      border: 1px solid $p-sidebar-line;
      border-radius: 10px;
      box-shadow: 0 12px 24px -6px rgba(0, 0, 0, 0.5);
      z-index: 50;

      /* cat-head 와 flyout 사이 4px 간격을 가리는 투명 브릿지 —
         마우스가 이 영역을 지나갈 때도 hover 상태 유지 */
      &::before {
        content: '';
        position: absolute;
        top: 0;
        bottom: 0;
        left: -10px;
        width: 10px;
      }
    }
    .p-sidebar__cat:hover .p-sidebar__items { display: block; }

    .p-sidebar__items-title {
      padding: 6px 10px 8px;
      font-size: 10px;
      font-weight: 800;
      letter-spacing: 0.1em;
      text-transform: uppercase;
      color: $p-sidebar-mid;
      border-bottom: 1px solid $p-sidebar-line;
      margin-bottom: 6px;
    }
    .p-sidebar__item {
      padding: 8px 12px;
      font-size: 13px;
    }
    .p-sidebar__item-dot { display: none; }

    .p-sidebar__footer {
      padding: 14px 12px;
      justify-content: center;
      gap: 8px;
    }
    .p-sidebar__user { display: none; }
    .p-sidebar__avatar { margin: 0; }
  }
}
</style>
